// vergleichstabelle — der Baustein mit dem groessten Gewinn.
//
// Webflow kann Tabellen im Designer nicht sinnvoll pflegen: jede Zelle ist ein
// eigenes Element. Marianns Ratgeber haben drei bis vier Tabellen pro Seite mit
// bis zu fuenf Spalten. Von Hand sind das ueber hundert Klicks je Seite.
// Hier wird stattdessen der Rohtext eingefuegt — notfalls direkt die
// Markdown-Tabelle, die Claude ihr geschrieben hat.
//
// Der eigentliche Knackpunkt ist das Handy. Eine fuenfspaltige Tabelle passt
// auf kein Telefon. Es gibt genau zwei ehrliche Antworten darauf, und beide
// stehen zur Wahl: die Zeilen zu Karten aufbrechen (lesbar, aber die Spalten
// sind nicht mehr nebeneinander vergleichbar) oder seitlich scrollen lassen
// (der Vergleich bleibt, das Scrollen muss man merken). Standard sind Karten.
//
// Einstellungen am Wrapper:
//   data-mobil          "karten" (Standard) | "scrollen"
//   data-erste-spalte   "hervorheben" (Standard) | "normal"
//   data-beschriftung   Bildunterschrift, auch fuer Vorlesegeraete
import { el, leerHinweis } from '@lib/bausteine/dom';
import { zerlegeTabelle } from '@lib/bausteine/zerlege';
import { zeichneAus } from '@lib/bausteine/auszeichnung';

export function vergleichstabelle(wurzel: HTMLElement, quelle: string): Node {
  const tabelle = zerlegeTabelle(quelle);

  if (!tabelle) {
    return leerHinweis('Noch keine Zeilen. Erste Zeile ist die Kopfzeile, Spalten mit | trennen.');
  }

  const scrollen = (wurzel.getAttribute('data-mobil') ?? '').trim().toLowerCase() === 'scrollen';
  const ersteSpalteHervorheben =
    (wurzel.getAttribute('data-erste-spalte') ?? '').trim().toLowerCase() !== 'normal';
  const beschriftung = (wurzel.getAttribute('data-beschriftung') ?? '').trim();

  const kopf = el(
    'thead',
    null,
    el('tr', null, ...tabelle.kopf.map((zelle) => el('th', { scope: 'col' }, zeichneAus(zelle)))),
  );

  const rumpf = el(
    'tbody',
    null,
    ...tabelle.zeilen.map((zeile) =>
      el(
        'tr',
        null,
        ...zeile.map((zelle, spalte) => {
          // Die erste Spalte ist die Zeilenbeschriftung, also <th> und nicht
          // <td>. Das ist kein Schoenheitsdetail: Vorlesegeraete nennen damit
          // bei jeder Zelle, worum es in der Zeile geht.
          const istKopfspalte = spalte === 0 && ersteSpalteHervorheben;
          return el(
            istKopfspalte ? 'th' : 'td',
            {
              scope: istKopfspalte ? 'row' : null,
              // In der Kartenansicht verschwindet die Kopfzeile. Damit die
              // Werte trotzdem beschriftet bleiben, steht der Spaltenname hier
              // und wird per CSS davorgesetzt.
              'data-spalte': tabelle.kopf[spalte] ?? '',
            },
            zeichneAus(zelle),
          );
        }),
      ),
    ),
  );

  // Beim seitlichen Scrollen wird der Rahmen zur eigenen Region mit tabindex:
  // sonst kaeme man mit der Tastatur nicht an den rechten Teil der Tabelle
  // heran. Bei der Kartenansicht waere das nur eine leere Tabstop-Station.
  const rahmen = el(
    'div',
    {
      class: `eoe-tabelle__rahmen${scrollen ? ' eoe-tabelle__rahmen--scroll' : ''}`,
      tabindex: scrollen ? '0' : null,
      role: scrollen ? 'region' : null,
      'aria-label': scrollen ? beschriftung || 'Tabelle, seitlich scrollbar' : null,
    },
    el(
      'table',
      { class: `eoe-tabelle__grid${scrollen ? '' : ' eoe-tabelle__grid--karten'}` },
      kopf,
      rumpf,
    ),
  );

  return el(
    'figure',
    { class: 'eoe-reset eoe-tabelle' },
    beschriftung && el('figcaption', { class: 'eoe-tabelle__beschriftung' }, beschriftung),
    rahmen,
  );
}
